<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Reporte General de Garantías</h1>
      <div class="flex space-x-4 items-center">
        <input type="date" v-model="fecha_inicio" class="px-4 py-2 border rounded">
        <span>a</span>
        <input type="date" v-model="fecha_fin" class="px-4 py-2 border rounded">
        <button @click="cargarDatos" class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Consultar</button>
      </div>
    </div>

   

    <!-- Gráfica de Garantías por Empleado -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <h2 class="text-lg font-semibold mb-4">Distribución de Garantías por Empleado</h2>
      <div v-if="garantiasPorEmpleado.length > 0" class="h-80">
        <apexchart
          type="donut"
          :options="chartOptions"
          :series="chartSeries"
          height="100%"
        ></apexchart>
      </div>
      <div v-else class="h-40 flex items-center justify-center text-gray-400">
        {{ loading ? 'Cargando...' : 'No hay datos para mostrar' }}
      </div>
    </div>

    <!-- Resumen de Totales -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      <!-- Total Garantías -->
      <div class="bg-white rounded-lg shadow p-4 border border-gray-200">
        <p class="text-sm text-gray-500">Total Garantías</p>
        <p class="text-2xl font-bold">{{ formatCurrency(totals.total_garantias) }}</p>
        <p class="text-xs text-gray-500 mt-1">{{ totals.total_garantias_count }} unidades</p>
      </div>
      
      
      
      <!-- Empleado con más garantías -->
      <div class="bg-white rounded-lg shadow p-4 border border-gray-200">
        <p class="text-sm text-gray-500">Mayor N° de Garantías</p>
        <div v-if="totals.empleado_mas_garantias" class="mt-1">
          <p class="text-lg font-semibold">{{ totals.empleado_mas_garantias.nombre }}</p>
          <p class="text-sm text-gray-600">{{ totals.empleado_mas_garantias.total_garantias }} garantías</p>
        </div>
        <p v-else class="text-sm text-gray-500 mt-1">Sin datos</p>
      </div>
      
      <!-- Empleado con menos garantías -->
      <div class="bg-white rounded-lg shadow p-4 border border-gray-200">
        <p class="text-sm text-gray-500">Menor N° de Garantías</p>
        <div v-if="totals.empleado_menos_garantias" class="mt-1">
          <p class="text-lg font-semibold">{{ totals.empleado_menos_garantias.nombre }}</p>
          <p class="text-sm text-gray-600">{{ totals.empleado_menos_garantias.total_garantias }} garantías</p>
        </div>
        <p v-else class="text-sm text-gray-500 mt-1">Sin datos</p>
      </div>
    </div>

    
    
    <!-- Resumen del Período -->
    <div v-if="totals.resumen_periodo" class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-blue-50 rounded-lg shadow p-4 border border-blue-100">
        <p class="text-sm text-blue-700">Total Ventas Período</p>
        <p class="text-xl font-bold text-blue-800">{{ formatCurrency(totals.resumen_periodo.total_ventas) }}</p>
      </div>
      <div class="bg-green-50 rounded-lg shadow p-4 border border-green-100">
        <p class="text-sm text-green-700">Total Abonado</p>
        <p class="text-xl font-bold text-green-800">{{ formatCurrency(totals.resumen_periodo.total_abonado) }}</p>
      </div>
      <div class="bg-yellow-50 rounded-lg shadow p-4 border border-yellow-100">
        <p class="text-sm text-yellow-700">Total Pendiente</p>
        <p class="text-xl font-bold text-yellow-800">{{ formatCurrency(totals.resumen_periodo.total_pendiente) }}</p>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-xl font-semibold">Registro de Garantías</h2>
          <p class="text-gray-600">Visualización detallada de las garantías registradas en el sistema.</p>
        </div>
        <div class="text-right">
          <p class="text-sm text-gray-500">Total de registros</p>
          <p class="text-2xl font-bold">{{ garantias.length }}</p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
          <select v-model="filtroVendedor" class="w-full p-2 border rounded">
            <option value="">Todos</option>
            <option v-for="vendedor in vendedores" :key="vendedor.id" :value="vendedor.id">
              {{ vendedor.name }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="text-center py-10">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <p class="mt-2">Cargando datos...</p>
      </div>

      <div v-else-if="error" class="text-center py-10 text-red-500">
        <p>{{ error }}</p>
        <button @click="cargarDatos" class="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200">
          Reintentar
        </button>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full bg-white text-xs table-auto">
          <colgroup>
            <col class="w-24"> <!-- # Cotización Garantía -->
            <col class="w-24"> <!-- # Cotización Origen -->
            <col class="w-20"> <!-- # Pedido -->
            <col class="w-24"> <!-- Responsable -->
            <col class="w-32"> <!-- Cliente -->
            <col class="w-24"> <!-- Vendedor -->
            <col class="w-[100px]"> <!-- Garantía (fixed width) -->
            <col class="w-24"> <!-- Fecha Pedido -->
          </colgroup>
          <thead>
            <tr class="bg-gray-100">
              <th class="py-2 px-2 text-left"># Cot. Garantía</th>
              <th class="py-2 px-2 text-left"># Cot. Origen</th>
              <th class="py-2 px-2 text-left"># Pedido</th>
              <th class="py-2 px-2 text-left">Responsable</th>
              <th class="py-2 px-2 text-left">Cliente</th>
              <th class="py-2 px-2 text-left">Vendedor</th>
              <th class="py-2 px-2 text-left">Detalle Garantía</th>
              <th class="py-2 px-2 text-left">Fecha</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="garantiasPaginadas.length === 0">
              <td colspan="10" class="text-center py-4">No hay garantías que coincidan con los filtros.</td>
            </tr>
            <tr v-for="garantia in garantiasPaginadas" :key="garantia.id" class="hover:bg-gray-50 text-xs">
              <!-- Número de cotización -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="text-gray-700 font-medium">{{ garantia.id }}</div>
              </td>
              
              <!-- Cotización Origen -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="text-gray-600">{{ garantia.garantia || 'N/A' }}</div>
              </td>
              
              <!-- # Pedido -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="text-gray-700">{{ garantia.idventa || 'N/A' }}</div>
              </td>
              
              <!-- Responsable -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="text-gray-700">{{ garantia.responsable || 'N/A' }}</div>
                <div class="text-[10px] text-gray-400">ID: {{ garantia.idresponsable || 'N/A' }}</div>
              </td>
              
              <!-- Cliente -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="font-medium text-gray-800 truncate max-w-[120px]">{{ garantia.contacto || 'N/A' }}</div>
                <div class="text-[10px] text-gray-400">ID: {{ garantia.clientes_id || 'N/A' }}</div>
              </td>
              
              <!-- Vendedor -->
              <td class="py-1 px-2 border-b border-gray-100">
                <div class="text-gray-700 truncate max-w-[80px]">{{ garantia.vendedor || 'N/A' }}</div>
                <div class="text-[10px] text-gray-400">ID: {{ garantia.idvendedor || 'N/A' }}</div>
              </td>
              
              <!-- Garantía -->
              <td class="py-1 px-2 border-b border-gray-100 max-w-[100px] w-[100px]">
                <div v-if="garantia.detalle_garantia" class="text-gray-600 text-[10px] break-words">
                  {{ garantia.detalle_garantia }}
                </div>
                <div v-else class="text-[10px] text-gray-400">
                  Sin detalles
                </div>
              </td>
              
              <!-- Fecha de pedido -->
              <td class="py-1 px-2 border-b border-gray-100 whitespace-nowrap">
                <div class="text-gray-700">{{ formatDate(garantia.fecha_pedido) }}</div>
                <div class="text-[10px] text-gray-400">
                  {{ formatDateTime(garantia.fecha_pedido) }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-between items-center mt-4">
        <div class="text-sm text-gray-600">
          Mostrando {{ garantiasPaginadas.length }} de {{ garantiasFiltradas.length }} registros
        </div>
        <div class="flex space-x-2">
          <button class="px-3 py-1 border rounded" :disabled="paginaActual === 1" @click="paginaAnterior">Anterior</button>
          <span class="px-3 py-1">Página {{ paginaActual }} de {{ totalPaginas }}</span>
          <button class="px-3 py-1 border rounded" :disabled="paginaActual >= totalPaginas" @click="siguientePagina">Siguiente</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos.js';
import axios from 'axios';
import VueApexCharts from 'vue3-apexcharts';

export default {
  components: {
    apexchart: VueApexCharts,
  },
  name: 'ReporteGarantias',
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(endDate.getMonth() - 1);

    return {
      fecha_inicio: startDate.toISOString().split('T')[0],
      fecha_fin: endDate.toISOString().split('T')[0],
      loading: false,
      error: null,
      garantias: [],
      vendedores: [],
      filtroVendedor: '',
      elementosPorPagina: 10,
      paginaActual: 1,
      garantiasPorEmpleado: [],
      totals: {
        total_garantias: 0,
        total_abonos: 0,
        total_pendiente: 0,
        total_garantias_count: 0
      }
    };
  },
  computed: {
    chartSeries() {
      if (!this.garantiasPorEmpleado || !Array.isArray(this.garantiasPorEmpleado)) {
        return [];
      }
      return this.garantiasPorEmpleado.map(empleado => empleado.total_garantias || 0);
    },
    
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          }
        },
        labels: this.garantiasPorEmpleado?.map(emp => emp.nombre || 'Sin nombre') || [],
        colors: [
          '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
          '#EC4899', '#14B8A6', '#F97316', '#8B5A2B', '#A78BFA',
          '#06B6D4', '#F43F5E', '#84CC16', '#6366F1', '#F59E0B'
        ],
        legend: {
          position: 'right',
          horizontalAlign: 'center',
          itemMargin: {
            horizontal: 8,
            vertical: 4
          },
          fontSize: '13px',
          markers: {
            width: 12,
            height: 12,
            radius: 6,
            offsetX: -4
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  showAlways: true,
                  label: 'Total Garantías',
                  fontSize: '16px',
                  fontWeight: 600,
                  formatter: () => this.totals.total_garantias_count?.toString() || '0'
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              const total = this.garantiasPorEmpleado?.reduce((sum, item) => sum + (item.total_garantias || 0), 0) || 1;
              const percentage = ((value / total) * 100).toFixed(1);
              return `${value} garantías (${percentage}%)`;
            }
          },
          style: {
            fontSize: '12px',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val, { seriesIndex }) => {
            const total = this.garantiasPorEmpleado?.reduce((sum, item) => sum + (item.total_garantias || 0), 0) || 1;
            const percentage = ((this.garantiasPorEmpleado?.[seriesIndex]?.total_garantias || 0) / total * 100).toFixed(0);
            return percentage > 5 ? `${percentage}%` : '';
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          },
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 1,
            opacity: 0.45
          }
        },
        responsive: [{
          breakpoint: 1024,
          options: {
            chart: {
              height: 400
            },
            legend: {
              position: 'bottom'
            }
          }
        }, {
          breakpoint: 768,
          options: {
            chart: {
              height: 350
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center'
            }
          }
        }],
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1
            }
          },
          active: {
            filter: {
              type: 'darken',
              value: 0.1
            }
          }
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['#fff']
        },
        fill: {
          opacity: 0.9
        }
      };
    },
    vendedoresUnicos() {
      const vendedoresMap = new Map();
      this.garantias.forEach(g => {
        if (g.idvendedor && g.vendedor) {
          vendedoresMap.set(g.idvendedor, {
            id: g.idvendedor,
            name: g.vendedor
          });
        }
      });
      return Array.from(vendedoresMap.values());
    },
    garantiasFiltradas() {
      return this.garantias.filter(g => {
        const cumpleVendedor = !this.filtroVendedor || g.idvendedor == this.filtroVendedor;
        return cumpleVendedor;
      });
    },
    totalPaginas() {
      return Math.ceil(this.garantiasFiltradas.length / this.elementosPorPagina);
    },
    garantiasPaginadas() {
      const inicio = (this.paginaActual - 1) * this.elementosPorPagina;
      const fin = inicio + this.elementosPorPagina;
      return this.garantiasFiltradas.slice(inicio, fin);
    },
  },
  watch: {
    filtroStatus() {
      this.paginaActual = 1;
    },
    filtroTipo() {
      this.paginaActual = 1;
    },
  },
  methods: {
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      try {
        const response = await reportesNuevos.REPORTE_GARANTIAS({
          fecha_inicio: this.fecha_inicio,
          fecha_fin: this.fecha_fin,
        });
        
        if (response.errors) {
          this.error = Object.values(response.errors).join(', ');
          this.garantias = [];
          return;
        }
        
        // Actualizar datos y totales
        this.garantiasPorEmpleado = Array.isArray(response.garantias_por_empleado) 
          ? response.garantias_por_empleado 
          : [];
        this.garantias = response.data || [];
        this.totals = {
          total_garantias: response.totals?.total_garantias || 0,
          total_abonos: response.totals?.total_abonos || 0,
          total_pendiente: response.totals?.total_pendiente || 0,
          total_garantias_count: response.totals?.total_garantias_count || 0,
          empleado_mas_garantias: response.totals?.empleado_mas_garantias || null,
          empleado_menos_garantias: response.totals?.empleado_menos_garantias || null,
        };
        
      } catch (error) {
        console.error('Error al cargar garantías:', error);
        this.error = error.response?.data?.message || 'No se pudieron cargar las garantías. Intente de nuevo más tarde.';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '$0.00';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    getStatusBadgeClass(status) {
      const classes = {
        'Pendiente': 'bg-yellow-100 text-yellow-800',
        'En proceso': 'bg-blue-100 text-blue-800',
        'Completada': 'bg-green-100 text-green-800',
        'Rechazada': 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },
    getTipoBadgeClass(tipo) {
      const classes = {
        'Fábrica': 'bg-purple-100 text-purple-800',
        'Comercial': 'bg-indigo-100 text-indigo-800',
        'Otro': 'bg-gray-100 text-gray-800'
      };
      return classes[tipo] || 'bg-gray-100 text-gray-800';
    },
    getOrigenBadgeClass(origen) {
      const classes = {
        'web': 'bg-blue-100 text-blue-800',
        'local': 'bg-green-100 text-green-800',
        'llamada': 'bg-yellow-100 text-yellow-800',
        'whatsapp': 'bg-green-100 text-green-800',
        'facebook': 'bg-blue-600 text-white',
        'instagram': 'bg-pink-100 text-pink-800',
        'recomendacion': 'bg-purple-100 text-purple-800'
      };
      // Convert to lowercase and replace spaces with underscores for matching
      const key = (origen || '').toLowerCase().replace(/\s+/g, '_');
      return classes[key] || 'bg-gray-100 text-gray-800';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        timeZone: 'America/Mexico_City'
      };
      return new Date(dateString).toLocaleDateString('es-MX', options);
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      const options = {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Mexico_City'
      };
      return new Date(dateTimeString).toLocaleTimeString('es-MX', options);
    },
    paginaAnterior() {
      if (this.paginaActual > 1) this.paginaActual--;
    },
    siguientePagina() {
      if (this.paginaActual < this.totalPaginas) this.paginaActual++;
    },
  },
  mounted() {
    this.cargarDatos();
  },
  created() {
    // Initialize garantiasPorEmpleado as an empty array if not already set
    if (!this.garantiasPorEmpleado) {
      this.garantiasPorEmpleado = [];
    }
  },
};
</script>

<style scoped>
/* Estilos específicos para este componente */
</style>
